import type { RouteById, SuccessOf } from "@volgo/core/contract";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useConfig } from "../app/config";
import { useSession } from "../session";

export type DownloadSummary = SuccessOf<RouteById<"downloads.summary">>;

/**
 * My queued and running downloads and the global slots, for the sidebar and the bottom tabs (46 Spec 3.5).
 * Asked when the frame first draws, when the page becomes visible again, then every `summaryActiveMs` while I
 * have jobs waiting or running and every `summaryIdleMs` otherwise; never while the page is hidden.
 */
export function useDownloadSummary() {
  const { client } = useSession();
  const { summaryActiveMs, summaryIdleMs } = useConfig();
  const [summary, setSummary] = useState<DownloadSummary | null>(null);
  const [round, setRound] = useState(0);
  const refresh = useCallback(() => setRound((r) => r + 1), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `round` is the reason to ask again
  useEffect(() => {
    if (document.visibilityState === "hidden") return; // the visibility listener starts the next round
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    client.call("downloads.summary").then(
      (next) => {
        if (!alive) return;
        setSummary(next);
        timer = setTimeout(
          refresh,
          next.mine.queued + next.mine.running > 0 ? summaryActiveMs : summaryIdleMs,
        );
      },
      () => {
        // the summary is a convenience: keep the last one and try again later
        if (alive) timer = setTimeout(refresh, summaryIdleMs);
      },
    );
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [client, round, refresh, summaryActiveMs, summaryIdleMs]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [refresh]);

  return { summary, refresh };
}

export const DownloadSummaryContext = createContext<ReturnType<typeof useDownloadSummary>>({
  summary: null,
  refresh: () => {},
});

/** Tells the sidebar that a download was just asked for, so its numbers follow at once. */
export const useDownloadSummaryRefresh = () => useContext(DownloadSummaryContext).refresh;

/** Mine queued + running: the badge number; 0 draws no badge. */
export const activeCount = (summary: DownloadSummary | null) =>
  summary ? summary.mine.queued + summary.mine.running : 0;
